import { Inject, inject, Injectable, LOCALE_ID } from '@angular/core';

import { fromEvent, Observable, of, take } from 'rxjs';
import { UAParser } from 'ua-parser-js';

import { environment } from '@fantaz/shared/app-config';
import {
  CasualTournamentEntity,
  Leader,
  PrizeDetails,
  TsevoSessionResponse,
} from '@fantaz/shared/data-access/models';
import { PlacePipe } from '@fantaz/shared/pipes';
import { CurrencyService } from '@fantaz/shared/utils';
import { ToastService } from '@fantaz/shared/utils/toast';

export type TSEVOData = { apiKey: string; trustedUrl: string };
@Injectable({ providedIn: 'root' })
export class UtilsService {
  #currencyService: CurrencyService = inject(CurrencyService);
  onWindowLoad$: Observable<Event>;
  window = window;
  constructor(
    private toastService: ToastService,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    this.onWindowReady();
  }

  getEventImageUrl(gameKey: string, ext: string) {
    let version = 'v1';
    let gameKeyUri = '';
    let dotVersion = '';
    if (gameKey.indexOf('.v') >= 0) {
      gameKeyUri = gameKey.substring(0, gameKey.indexOf('.v'));
      dotVersion = gameKey.substring(gameKey.indexOf('.'), gameKey.length);
      version = dotVersion.substring(1, dotVersion.length);
    }
    return (
      environment.gameFileHost +
      gameKeyUri +
      '/' +
      version +
      '/events/' +
      gameKeyUri +
      ext
    );
  }

  parseTSEVOData(depositSession: TsevoSessionResponse | null): TSEVOData {
    let result = { apiKey: '', trustedUrl: '' };
    if (
      depositSession &&
      depositSession.SessionURL &&
      depositSession.SessionID
    ) {
      const trustedUrlArr =
        // eslint-disable-next-line no-useless-escape
        /(http|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/.exec(
          depositSession.SessionURL,
        );
      const apiKey = depositSession.SessionID;
      const trustedUrl = trustedUrlArr ? trustedUrlArr[0] : '';
      result = { apiKey, trustedUrl };
    }
    return result;
  }

  onWindowReady() {
    this.onWindowLoad$ = fromEvent(this.window, 'load').pipe(take(1));
  }

  getPrizeDetails(tournament: CasualTournamentEntity) {
    if (tournament.lifeExperience) {
      if (
        tournament &&
        tournament.eventSchedules &&
        tournament.eventSchedules.length > 0
      ) {
        if (
          tournament.eventSchedules[0].prizeDetails &&
          tournament.eventSchedules[0].prizeDetails.length > 0
        ) {
          const prizeDetails = tournament.eventSchedules[0].prizeDetails.filter(
            (t) => t.rank === 1,
          );
          if (prizeDetails.length > 0) {
            tournament.prizeDetails = prizeDetails[0];
          }
        }
      }
    }
  }

  isInAppBrowser(browser?: string) {
    const parser = new UAParser();
    browser =
      parser && parser.getBrowser() && parser.getBrowser().name?.toLowerCase();
    const isTiktok = window.navigator.userAgent
      .toLowerCase()
      .includes('bytedance');
    return browser === 'facebook' || browser === 'instagram' || isTiktok;
  }

  isCheckCurrentBrowser(browserName: string) {
    const parser = new UAParser();
    const browser =
      parser && parser.getBrowser() && parser.getBrowser().name?.toLowerCase();
    if (browserName === 'tiktok') {
      return window.navigator.userAgent.toLowerCase().includes('bytedance');
    }
    return browser === browserName;
  }

  copyToClipboard(text: string) {
    this.window.navigator.clipboard.writeText(text).then(() => {
      const message = 'The url link was copied!';
      this.toastService.openSnackBar(message || 'Undefined client error', {
        duration: 5000,
        horizontalPosition: 'center',
      });
    });
  }

  getGameIframeEle(): Observable<HTMLIFrameElement> {
    const iframe = document.getElementById(
      'playInstanceIframe',
    ) as HTMLIFrameElement;
    return of(iframe);
  }

  isMessageValid(message: any, key: string) {
    if (typeof message === 'string' || message instanceof String) {
      return message.indexOf(key) > -1;
    }
    return false;
  }

  getPlaceCustomElement(
    element: Leader,
    eventSchedulePrizes: Map<number, PrizeDetails> | null,
    showScores: boolean,
    isLEP: boolean,
  ) {
    let winnerElement = '';
    const prize = eventSchedulePrizes?.get(Number(element.rank));
    if (showScores) {
      let src = '';
      if (prize) {
        if (isLEP) {
          src =
            (typeof prize?.prizeImageAvatar === 'string'
              ? prize?.prizeImageAvatar
              : prize?.prizeImageAvatar?.url) || '';
          winnerElement = `<img class="prize-avatar" src="${src}" alt="Place"/>`;
        } else {
          const pValue = this.#currencyService.convertDollar(
            Number(prize.prizeValue),
          );
          src = '/shared/svg/cash-prize-icon.svg';
          winnerElement = `<div><span class="prize-value">${pValue}</span><span class="code"> USD</span></div>`;
        }
      }
    }
    const place = new PlacePipe().transform(element.rank);
    return `
      <div class="place-ctn">
      <span class="place-value">${place}</span>
       ${winnerElement}
      </div>
      `;
  }

  getPlaceCustomPopover(
    l: Leader,
    eventSchedulePrizes: Map<number, PrizeDetails> | null,
    isLEP: boolean,
  ) {
    const prize = eventSchedulePrizes?.get(Number(l.rank));
    let icon = '';
    let label = '';
    let value = '';
    let details = '';
    let classImg = '';
    if (prize) {
      l.mobPopoverOpen = false;
      l.deskPopoverOpen = false;
      value = this.#currencyService.convertDollar(Number(prize.prizeValue));
      if (isLEP) {
        l.isLEP = true;
        label = prize.prizeName || '';
        icon =
          (typeof prize.prizeImageAvatar === 'string'
            ? prize.prizeImageAvatar
            : prize.prizeImageAvatar?.url) || '';
        classImg = 'prize-img';
        details = `<div class="details"><span class="details-desc">Market value:</span> <span class="currency mat-body-1">${value} USD</span>
        </div>`;
      } else {
        label = 'Cash Prize';
        icon = `/shared/svg/cash-prize-icon.svg`;
        details = `<div class="details"><span class="currency mat-body-1">${value} USD</span>
        </div>`;
      }
    }
    const img = `<img src="${icon}" class="${classImg}"/>`;
    return `
    <div class="place-details">
    ${img}
    <p class="mat-subtitle-1 subtitle">${label}</p>
    ${details}
    </div>
   `;
  }
}
